import {useCallback} from 'react';
import type {Media} from 'api/types';
import {useLocalization} from 'localization';
import {getMediaUrl} from 'api/media';
import {useGetTokens} from 'services/auth';
import {useSnacks} from 'components/snacks';

export const usePreviewFiles = (
  files: Media[] | undefined,
  mock?: boolean,
): {
  previewAll: () => Promise<void>;
} => {
  const {t} = useLocalization();
  const getTokens = useGetTokens();
  const {errorSnack} = useSnacks();

  /**
   * Browsers are not happy with triggering multiple "popups" within a short
   * timespan, so we create a small delay. Users probably also has to whitelist
   * ebba as allowed to trigger popups like this.
   */
  const openWithDelay = useCallback((urls: string[]) => {
    for (let i = 0; i < urls.length; i++) {
      setTimeout(() => {
        window.open(urls[i], '_blank', 'noopener,noreferrer');
      }, i * 100);
    }
  }, []);

  const previewAll = useCallback(() => {
    const mediaUrls: Promise<string>[] = [];
    if (!files || !files.length) {
      return Promise.resolve();
    }

    files.forEach(file => {
      mediaUrls.push(getMediaUrl.apply(null, [file.id, getTokens, mock]));
    });

    return Promise.all(mediaUrls)
      .then(urls => openWithDelay(urls))
      .catch(() => {
        errorSnack(t('error.files.failedToOpen'));
      });
  }, [files, getTokens, mock, openWithDelay, errorSnack, t]);

  return {
    previewAll,
  };
};
